import type { AlphaRun } from "@nlc/shared-types";

import { useNavigate } from "@tanstack/react-router";
import { ChevronUp, Download, RefreshCw } from "lucide-react";
import { useState } from "react";

import { Button } from "@/components/button";

import { alphaDesignName } from "../alphaDesignName";
import { ALPHA_MOCKED_STYLES } from "../generation/alphaMockedStyles";
import { useCreateAlphaBatch } from "../hooks/useCreateAlphaBatch";
import { AlphaDesignCard } from "./AlphaDesignCard";
import { GenerateNewAdsDialog } from "./GenerateNewAdsDialog";

function failedMessage({ batch }: AlphaRun): string {
  return batch === 1
    ? "We couldn't generate this ad. Your creation credit was given back."
    : "We couldn't generate this ad.";
}

export function AlphaReviewScreen({
  requestId,
  runs,
}: {
  requestId: number;
  runs: AlphaRun[];
}) {
  const navigate = useNavigate();
  const [selectedIds, setSelectedIds] = useState<ReadonlySet<string>>(
    new Set(),
  );
  const [isDialogOpen, setIsDialogOpen] = useState(false);

  const firstBatch = runs.find(({ batch }) => batch === 1);
  const hasSecondBatch = runs.some(
    ({ batch, status }) => batch === 2 && status !== "failed",
  );
  const canGenerateNewAds =
    firstBatch?.status === "delivered" && !hasSecondBatch;

  const newBatch = useCreateAlphaBatch({
    creationId: firstBatch?.creationId ?? "",
    onCreated: () => setIsDialogOpen(false),
    requestId,
  });

  const cards = runs.flatMap((run) =>
    run.designs.map((design) => ({
      design,
      name: alphaDesignName(run, design),
      run,
      styleTag:
        ALPHA_MOCKED_STYLES[design.planIndex % ALPHA_MOCKED_STYLES.length]!,
    })),
  );
  const adCount = cards.filter(({ design }) => design.imageUrl).length;
  const failedRuns = runs.filter(
    ({ designs, status }) => status === "failed" && designs.length === 0,
  );

  const toggleSelected = (designId: string) =>
    setSelectedIds((current) => {
      const next = new Set(current);

      if (!next.delete(designId)) next.add(designId);

      return next;
    });
  const preview = (designId: string) =>
    void navigate({
      params: { designId, requestId },
      to: "/requests/$requestId/alpha/designs/$designId",
    });
  const edit = (designId: string) =>
    void navigate({
      params: { designId, requestId },
      to: "/requests/$requestId/alpha/designs/$designId/edit",
    });

  return (
    <>
      <div className="flex items-end justify-between gap-xl px-3xl pb-md pt-3xl">
        <div className="flex max-w-[680px] flex-col gap-md">
          <h1 className="text-display-sm font-extrabold text-text-primary">
            Here are your {adCount} ads.
          </h1>
          <p className="text-lg text-text-tertiary">
            Click the ads you like to select them. Use the magnifier to see one
            full size, or hit Edit to mark it up and let the AI redo it.
          </p>
        </div>
        {canGenerateNewAds && (
          <Button
            color="gray"
            onPress={() => setIsDialogOpen(true)}
            size="lg"
            variant="secondary"
          >
            <RefreshCw size={18} />
            Generate new ads
          </Button>
        )}
      </div>
      {failedRuns.map(({ batch, runId }) => (
        <p className="px-3xl pt-md text-sm text-text-error-primary" key={runId}>
          {batch === 1
            ? "We couldn't generate your ads. Your creation credit was given back."
            : "We couldn't generate your new ads."}
        </p>
      ))}
      <div className="grid grid-cols-[repeat(auto-fill,minmax(170px,1fr))] gap-lg px-3xl pb-3xl pt-xl">
        {cards.map(({ design, name, run, styleTag }) => (
          <AlphaDesignCard
            design={design}
            failedMessage={failedMessage(run)}
            isSelected={selectedIds.has(design.designId)}
            key={design.designId}
            name={name}
            onEdit={() => edit(design.designId)}
            onPreview={() => preview(design.designId)}
            onToggleSelected={() => toggleSelected(design.designId)}
            styleTag={styleTag}
          />
        ))}
      </div>
      <div className="flex items-center justify-between rounded-b-2xl border-t border-border-secondary bg-bg-secondary px-3xl py-xl">
        <div className="flex items-center gap-lg">
          <span className="text-lg font-extrabold text-text-primary">
            {selectedIds.size} selected
          </span>
          <Button
            color="gray"
            isDisabled={selectedIds.size === 0}
            onPress={() => setSelectedIds(new Set())}
            variant="tertiary"
          >
            Clear
          </Button>
        </div>
        <Button isDisabled size="xl" variant="primary">
          <Download size={18} />
          Download selected
          <ChevronUp size={16} />
        </Button>
      </div>

      <GenerateNewAdsDialog
        errorMessage={newBatch.errorMessage}
        isGenerating={newBatch.isCreating}
        isOpen={isDialogOpen}
        onGenerate={newBatch.create}
        onOpenChange={(isOpen) => {
          setIsDialogOpen(isOpen);

          if (!isOpen) newBatch.reset();
        }}
      />
    </>
  );
}
